import React from "react";
import { canAssignRole, roleUnavailableReason } from "../../../utils/roleUtils";

const ROLE_OPTIONS = [
  { value: "super_admin", label: "Super Admin", description: "Full workspace access and user management" },
  { value: "admin", label: "Admin", description: "Manage workspace content and settings" },
  { value: "normal_user", label: "User", description: "View and interact with workspace content" },
];

// An unavailable role stays VISIBLE rather than hidden: a hidden option reads
// as a missing feature. The wording comes from roleUtils so this picker and
// the User Management Role column explain the same refusal identically.

/**
 * Role radio group (Super Admin / Admin / User). Presentational — the container
 * owns `role` state and applies `onChange`.
 *
 * `assignableRoles` is the server-computed grant ladder (`assignable_roles` on
 * the roster response). Roles outside it render disabled with the reason
 * stated, instead of offering a grant the API refuses. `null` means the ladder
 * is not known yet and disables every role — failing open would reintroduce
 * exactly the dead-end affordance this prop exists to remove.
 *
 * @param {object} props
 * @param {string} props.role
 * @param {(role: string) => void} props.onChange
 * @param {string[]|null} props.assignableRoles
 */
export default function RolePicker({ role, onChange, assignableRoles }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-gray-700 text-sm font-bold font-['Inter']">Role</label>
      <div className="flex flex-col gap-1">
        {ROLE_OPTIONS.map((roleOption) => {
          const disabled = !canAssignRole(roleOption.value, assignableRoles);
          return (
            <label
              key={roleOption.value}
              className={`flex items-center py-3 px-3 rounded-lg transition-all duration-200 ease-in-out group border-2 ${
                disabled
                  ? "cursor-not-allowed opacity-60 border-transparent"
                  : role === roleOption.value
                    ? "cursor-pointer border-blue-200 bg-blue-50 hover:bg-blue-100"
                    : "cursor-pointer border-transparent hover:bg-gray-50 hover:border-gray-200"
              }`}
              onClick={() => !disabled && onChange(roleOption.value)}
            >
              <input
                type="radio"
                name="role"
                value={roleOption.value}
                checked={role === roleOption.value}
                disabled={disabled}
                onChange={() => onChange(roleOption.value)}
                className="w-4 h-4 text-blue-600 transition-colors duration-200 focus:ring-blue-500 focus:ring-2 disabled:cursor-not-allowed"
              />
              <div className="ml-3 flex flex-col">
                <span
                  className={`text-sm font-medium font-['Inter'] transition-colors duration-200 ${
                    disabled
                      ? "text-gray-500"
                      : role === roleOption.value
                        ? "text-blue-900"
                        : "text-gray-700"
                  }`}
                >
                  {roleOption.label}
                </span>
                <span className="text-xs font-normal font-['Inter'] transition-colors duration-200 text-gray-500 mt-0.5">
                  {disabled
                    ? roleUnavailableReason(roleOption.value, assignableRoles)
                    : roleOption.description}
                </span>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}
